import {Align, FlexDirection, Justify, PositionType} from '@wonderlandengine/react-ui';
import {
    Column,
    Container,
    Image,
    MaterialContext,
    Panel,
    Panel9Slice,
    PanelProps,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Emitter, Material} from '@wonderlandengine/api';
import {insertNewlines, parseColor} from '../utils/utils.js';
import {NotifyPropertyChanged} from '../../utils/NotifyPropertyChanged.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {MenuButton} from '../components/menu-button.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {Localization} from '../../utils/Localization.js';
import {PauseUnpause, PauseUnpauseAction} from '../../components/pause-unpause.js';
import {Window} from '../components/window.js';
import {WindowHeader} from '../components/window-header.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {isSoundTurnedOn, toggleSound, ToggleType} from '../utils/toggleSound.js';
import {restartGame} from '../utils/restartGameButton.js';
import {goToMainMenuConfirmation} from '../utils/gotoMainMenuConfirmation.js';
import {MissionDetails} from './mission-requirements.js';
import {GameFlowController} from '../../components/game-flow-controller.js';

function usePauseWindow(): [
    () => void,
    Material,
    Material,
    boolean,
    () => void,
    boolean,
    () => void,
] {
    //const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);
    const material = mats.panelMaterialTextured;
    const backgroundMaterial = useMemo(() => {
        const m = material!.clone() as FlatOpaqueTextured;
        m.setColor(parseColor(colorSwatch.DisabledButtonText));
        return m;
    }, []);
    const backgroundMaterial2 = useMemo(() => {
        const m = material!.clone() as FlatOpaqueTextured;
        m.setColor(parseColor(colorSwatch.SubTitleBackground));
        return m;
    }, []);

    const onPlayClick = () => {
        PauseUnpause.instance.onClick(PauseUnpauseAction.Unpause);
    };

    const [soundEnabled, setSoundEnabled] = useState(isSoundTurnedOn(ToggleType.Sound));
    const [musicEnabled, setMusicEnabled] = useState(isSoundTurnedOn(ToggleType.Music));

    const toggleMusic = () => {
        toggleSound(ToggleType.Music);
        setMusicEnabled(isSoundTurnedOn(ToggleType.Music));
    };

    const toggleAudio = () => {
        toggleSound(ToggleType.Sound);
        setSoundEnabled(isSoundTurnedOn(ToggleType.Sound));
    };

    return [
        onPlayClick,
        backgroundMaterial,
        backgroundMaterial2,
        soundEnabled,
        toggleAudio,
        musicEnabled,
        toggleMusic,
    ];
}

export const PauseWindow = (props: React.PropsWithChildren<PanelProps> & {}) => {
    const [
        onPlayClick,
        backgroundMaterial,
        backgroundMaterial2,
        soundEnabled,
        toggleAudio,
        musicEnabled,
        toggleMusic,
    ] = usePauseWindow();

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={72}>
                <Window width={590} height={750}>
                    <Column
                        margin={20}
                        height={710}
                        width={550}
                        alignSelf={Align.Center}
                        rowGap={8}
                    >
                        <WindowHeader title={Localization.instance.get('Pause')} />
                        <Container width="100%">
                            <Text width="100%" fontSize={28} textAlign="right">
                                {Localization.instance.get(
                                    GameModeType[GameFlowController.instance.gameMode]
                                )}
                            </Text>
                        </Container>
                        <Panel
                            height={500}
                            width="100%"
                            backgroundColor={colorSwatch.MalPortraitBackground}
                            rounding={0}
                            justifyContent={Justify.FlexStart}
                            alignItems={Align.Stretch}
                            alignContent={Align.Stretch}
                        >
                            <Column justifyContent={Justify.Center} gap={10}>
                                <Panel
                                    alignSelf={Align.Center}
                                    marginTop={20}
                                    height={50}
                                    width={500}
                                    rounding={0}
                                    backgroundColor={colorSwatch.SubTitleBackground}
                                    justifyContent={Justify.Center}
                                >
                                    <Text
                                        top={8}
                                        width="100%"
                                        fontSize={28}
                                        textAlign="center"
                                    >
                                        {Localization.instance.get('Missions')}
                                    </Text>
                                </Panel>

                                <Container
                                    marginTop={10}
                                    height={390}
                                    justifyContent={Justify.SpaceBetween}
                                    alignItems={Align.Center}
                                >
                                    <MissionDetails
                                        width={500}
                                        condense={true}
                                        missionId={0}
                                    ></MissionDetails>
                                    <MissionDetails
                                        width={500}
                                        condense={true}
                                        missionId={1}
                                    ></MissionDetails>
                                    <MissionDetails
                                        width={500}
                                        condense={true}
                                        missionId={2}
                                    ></MissionDetails>
                                </Container>
                            </Column>
                        </Panel>
                        <Row
                            width={550}
                            height={100}
                            alignItems={Align.Center}
                            alignContent={Align.Center}
                            justifyContent={Justify.SpaceAround}
                        >
                            <MenuButton
                                width={80}
                                height={80}
                                color={colorSwatch.CancelButton}
                                icon={UiTexturesManager.instance.home}
                                iconHeight={50}
                                iconWidth={50}
                                onClick={goToMainMenuConfirmation}
                            ></MenuButton>
                            <MenuButton
                                width={80}
                                height={80}
                                icon={UiTexturesManager.instance.restart}
                                color={colorSwatch.PauseRestart}
                                iconHeight={50}
                                iconWidth={50}
                                onClick={restartGame}
                            ></MenuButton>
                            <MenuButton
                                width={180}
                                height={90}
                                color={colorSwatch.AcceptButton}
                                fontSize={28}
                                margin={6}
                                iconWidth={26}
                                iconHeight={28}
                                alignIcon="right"
                                icon={UiTexturesManager.instance.arrow}
                                onClick={() => onPlayClick()}
                            >
                                {Localization.instance.get('Play')}
                            </MenuButton>
                            <MenuButton
                                width={80}
                                height={80}
                                color={colorSwatch.MusicButton}
                                icon={
                                    musicEnabled
                                        ? UiTexturesManager.instance.musicIcon
                                        : UiTexturesManager.instance.musicIconDisabled
                                }
                                onClick={toggleMusic}
                                iconHeight={50}
                                iconWidth={50}
                            ></MenuButton>
                            <MenuButton
                                width={80}
                                height={80}
                                color={colorSwatch.MusicButton}
                                icon={
                                    soundEnabled
                                        ? UiTexturesManager.instance.soundSpeaker
                                        : UiTexturesManager.instance.soundSpeakerDisabled
                                }
                                onClick={toggleAudio}
                                iconHeight={50}
                                iconWidth={50}
                            ></MenuButton>
                        </Row>
                    </Column>
                </Window>
            </Column>
        </Container>
    );
};

PauseWindow.displayName = 'PauseWindow';
